import { useMutation } from '@tanstack/react-query';

import { _deleteFundInvitation, _deleteFundMember } from '@/api/fund/mutations';
import { optimisticDeleteFundInvitation } from '@/api/fund/set-query-data';
import { useGlobalModal } from '@/global-hooks/use-global-modal';
import { Modal } from '@/shared/design-system';
import { useEntitySlice } from '@/stores/selectors';

import { CONFIRM_MODAL_MAX_WIDTH } from './confirm-modal.constants';

export const FUND_REMOVE_USER_BODY =
  'This action will revoke their role and access to this fund. The user will remain a member of the entity but will no longer be associated with this fund.';

export type FundRemoveUserModalProps = {
  fundId: string;
  memberId: string;
  invitationId: string;
  fundName: string;
  email: string;
};

export const FundRemoveUserModal = () => {
  const { open, handleClose, props } = useGlobalModal('FUND_REMOVE_USER_MODAL');
  const { stepForms } = useEntitySlice();
  const { mutate: deleteMember } = useMutation({ mutationFn: _deleteFundMember });
  const { mutate: deleteFundInvitation } = useMutation({ mutationFn: _deleteFundInvitation });

  const handleRemove = () => {
    if (props?.fundId && (props?.memberId || props?.invitationId)) {
      const onSuccess = () => {
        optimisticDeleteFundInvitation({
          entityId: stepForms.id,
          email: props.email,
          fundId: props.fundId,
        });

        handleClose();
      };

      if (props?.memberId) {
        deleteMember(
          { id: props.fundId, memberId: props.memberId },
          {
            onSuccess,
          },
        );
      } else {
        deleteFundInvitation(
          { id: props.fundId, invitationId: props.invitationId },
          {
            onSuccess,
          },
        );
      }
    }
  };

  return (
    <Modal
      open={open}
      onOpenChange={(isOpen) => !isOpen && handleClose()}
      showClose
      maxWidth={CONFIRM_MODAL_MAX_WIDTH}
      title={`Are you sure you want to remove this user from ${props?.fundName}?`}
      description={FUND_REMOVE_USER_BODY}
      primaryAction={{ label: 'Remove', color: 'destructive', onClick: handleRemove }}
      secondaryAction={{ label: 'Cancel', onClick: handleClose }}
    >
      <></>
    </Modal>
  );
};
